import type { ComponentPropsWithRef } from "react";

import {
  Command as CommandEnum,
  CommandMan,
  CommandType,
  State as StateEnum,
  type CommandAutomate,
  type State as StateType,
} from "@eisox/api/core-api";
import { cn, Select, Slider } from "@eisox/design-system";

import WarningIcon from "~/assets/boilerroomWarning.svg?react";
import Commutator from "~/assets/commutator.svg?react";
import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";

import { getActuatorState, getSpeedLabel } from "../helpers";

/**
 * Presentational primitives of the actuator command dialogs (fan/damper/coil/CTA), ported from the
 * legacy `ActuatorDialog` compound. Each dialog owns its Modal + form and composes these.
 */

type OnOffType = "on/off" | "open/close";

const allOrNothingLabel = (type: OnOffType, value: number): string => {
  if (type === "open/close") return value === 1 ? m.open() : m.closed();
  return value === 1 ? m.on() : m.off();
};

/* -------------------------------------------------------------------------------------------------
 * Group / Item
 * -----------------------------------------------------------------------------------------------*/

interface GroupProps extends ComponentPropsWithRef<"div"> {
  label: string;
}

const Group: React.FC<GroupProps> = ({ label, className, children, ...props }) => (
  <div className={cn("gap-1.25 flex flex-col", className)} {...props}>
    <span className="font-medium text-base">{label}</span>
    {children}
  </div>
);

Group.displayName = "ActuatorDialog.Group";

interface ItemProps extends ComponentPropsWithRef<"div"> {
  label: string;
}

const Item: React.FC<ItemProps> = ({ label, className, children, ...props }) => (
  <div className={cn("gap-2.5 px-2.5 flex items-center text-base", className)} {...props}>
    <span>{label}</span>
    {children}
  </div>
);

Item.displayName = "ActuatorDialog.Item";

/* -------------------------------------------------------------------------------------------------
 * Command
 * -----------------------------------------------------------------------------------------------*/

interface CommandProps {
  commandMan: CommandMan | undefined;
  value?: number;
  onValueChange?: (value: number) => void;
  /** Auto/forcing values — valve commands are inverted vs the other actuators. */
  options?: { value: number; name: string }[];
  disabled?: boolean;
  className?: string;
}

const Command: React.FC<CommandProps> = ({
  commandMan,
  value,
  onValueChange,
  options: optionsProp,
  disabled,
  className,
}) => {
  const options = optionsProp ?? [
    { value: CommandEnum.AUTO, name: m.actuator_state({ state: "auto" }) },
    { value: CommandEnum.MANUAL, name: m.actuator_state({ state: "forcing" }) },
  ];

  if (commandMan === CommandMan.MANUAL) {
    return (
      <div className="gap-1.25 flex items-center">
        {m.actuator_state({ state: "manual" })}
        <Tooltip content={m.manual_commutator()}>
          <Commutator />
        </Tooltip>
      </div>
    );
  }

  return (
    <Select.Root multiple={false} value={value?.toString()} onValueChange={v => onValueChange?.(Number(v))}>
      <Select.Trigger className={cn("gap-1.25 flex items-center", className)} disabled={disabled}>
        <Select.Value />
      </Select.Trigger>
      <Select.Content>
        <Select.Viewport>
          {options.map(({ value, name }) => (
            <Select.Item key={value} value={value.toString()}>
              {name}
            </Select.Item>
          ))}
        </Select.Viewport>
      </Select.Content>
    </Select.Root>
  );
};

Command.displayName = "ActuatorDialog.Command";

/* -------------------------------------------------------------------------------------------------
 * Forcing
 * -----------------------------------------------------------------------------------------------*/

interface ForcingProps {
  type?: OnOffType;
  /** The dialog's pending command value: the forcing input only shows in forcing (`MANUAL`) mode. */
  command: number | undefined;
  commandMan: CommandMan | undefined;
  commandType: CommandType | undefined;
  value?: number;
  onValueChange?: (value?: number) => void;
  disabled?: boolean;
}

const Forcing: React.FC<ForcingProps> = ({
  type = "on/off",
  command,
  commandMan,
  commandType,
  value,
  onValueChange,
  disabled,
}) => {
  if (command !== CommandEnum.MANUAL || commandMan === CommandMan.MANUAL) return null;

  return (
    <>
      {commandType === CommandType.ALL_OR_NOTHING && (
        <Select.Root multiple={false} value={value?.toString()} onValueChange={v => onValueChange?.(Number(v))}>
          <Select.Trigger className="w-30" disabled={disabled}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Viewport>
              <Select.Item value="0">{allOrNothingLabel(type, 0)}</Select.Item>
              <Select.Item value="1">{allOrNothingLabel(type, 1)}</Select.Item>
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
      )}
      {commandType === CommandType.PERCENTAGE && (
        <>
          <Slider
            className="w-30"
            displayButtons={false}
            displayValue={false}
            value={value}
            onValueChange={onValueChange}
            min={0}
            max={100}
            step={1}
            disabled={disabled}
          />
          <span className="text-primary-blue">{value ?? "-"}%</span>
        </>
      )}
      {(commandType === CommandType.TWO_SPEED || commandType === CommandType.THREE_SPEED) && (
        <Select.Root multiple={false} value={(value ?? 0).toString()} onValueChange={v => onValueChange?.(Number(v))}>
          <Select.Trigger className="w-45" disabled={disabled}>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Viewport>
              <Select.Item value="0">{m.actuator_speed({ state: "0" })}</Select.Item>
              <Select.Item value="1">{m.actuator_speed({ state: "1" })}</Select.Item>
              <Select.Item value="2">
                {m.actuator_speed({ state: commandType === CommandType.THREE_SPEED ? "2" : "3" })}
              </Select.Item>
              {commandType === CommandType.THREE_SPEED && (
                <Select.Item value="3">{m.actuator_speed({ state: "3" })}</Select.Item>
              )}
            </Select.Viewport>
          </Select.Content>
        </Select.Root>
      )}
    </>
  );
};

Forcing.displayName = "ActuatorDialog.Forcing";

/* -------------------------------------------------------------------------------------------------
 * State / ActuatorState
 * -----------------------------------------------------------------------------------------------*/

interface StateProps extends ComponentPropsWithRef<"span"> {
  value?: string | number;
}

const State: React.FC<StateProps> = ({ value, className, ...props }) => (
  <span className={cn("italic", className)} {...props}>
    {value ?? "-"}
  </span>
);

State.displayName = "ActuatorDialog.State";

interface ActuatorStateProps extends ComponentPropsWithRef<"div"> {
  type?: OnOffType;
  commandType: CommandType;
  state: StateType | number | undefined;
  commandAutomate: CommandAutomate | number | undefined;
  commandMan: CommandMan | undefined;
  defect: boolean;
  missmatchDefect: boolean;
}

const ActuatorState: React.FC<ActuatorStateProps> = ({
  type = "on/off",
  commandType,
  state: stateProp,
  commandAutomate,
  commandMan,
  defect,
  missmatchDefect,
  className,
  children,
  ...props
}) => {
  const { state, warning } = getActuatorState({ state: stateProp, commandAutomate, commandMan, defect });

  return (
    <div className={cn("gap-2.5 flex items-center text-base", className)} {...props}>
      {state !== null && (
        <span className="italic">
          {commandType === CommandType.PERCENTAGE && `${state.toFixed(1)}%`}
          {commandType === CommandType.ALL_OR_NOTHING && allOrNothingLabel(type, state === StateEnum.ON ? 1 : 0)}
          {commandType === CommandType.TWO_SPEED && getSpeedLabel(CommandType.TWO_SPEED, state)}
          {commandType === CommandType.THREE_SPEED && getSpeedLabel(CommandType.THREE_SPEED, state)}
        </span>
      )}
      {(defect || missmatchDefect) && <span className="size-2.5 block rounded-full bg-primary-red" />}
      {warning && (
        <Tooltip content={warning}>
          <WarningIcon />
        </Tooltip>
      )}
      {children}
    </div>
  );
};

ActuatorState.displayName = "ActuatorDialog.ActuatorState";

export { ActuatorState, Command, Forcing, Group, Item, State };
export type { ActuatorStateProps, CommandProps, ForcingProps, GroupProps, ItemProps, StateProps };
